import { useCallback, useEffect, useRef, useState } from "react";
import {
    speechRecognitionConstructor,
    type SpeechRecognitionLike,
} from "../../../shared/domain/speechRecognition";
import {
    dictationLanguageOf,
    normalizeDictatedText,
} from "../../../shared/domain/dictationText";

export type DictationError = "denied" | "unsupported" | "failed";

export type Dictation = {
    /** False where the browser has no speech recognition — hide the control. */
    supported: boolean;
    listening: boolean;
    /** What has been heard but not yet settled, to show greyed as it forms. */
    interim: string;
    error: DictationError | null;
    start: () => void;
    stop: () => void;
};

/**
 * Dictating a meditation script instead of typing it.
 *
 * A guided meditation is written to be *said*, and therapists compose them out
 * loud — the rhythm is the content. Typing it forces the writing into a
 * register the reading then has to be dragged back out of.
 *
 * Only settled phrases are committed, through `onCommit`, so the caller
 * appends to the script it already holds and the therapist can edit as they
 * go; the interim text is exposed separately for the "hearing you" hint and is
 * never written into the field, which would fight their cursor.
 *
 * A phrase is committed once and only once. The API's result list is
 * cumulative and a later event's `resultIndex` can point back at a phrase that
 * already settled, so the hook counts what it has committed and never goes
 * back over it — otherwise the same sentence lands in the script twice. A
 * session that restarts numbers its results from zero, and the count with it.
 *
 * Browser support is genuinely partial (Chrome and Safari yes, Firefox no),
 * and the control is hidden rather than shown broken where it is missing —
 * the keyboard is right there.
 */
export function useDictation(
    onCommit: (text: string) => void,
    language: string = "es-ES"
): Dictation {
    const [listening, setListening] = useState(false);
    const [interim, setInterim] = useState("");
    const [error, setError] = useState<DictationError | null>(null);
    const [supported] = useState(() => speechRecognitionConstructor() !== null);

    const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
    // Read from the recogniser's callbacks, which are created once and would
    // otherwise keep calling the first render's handler.
    const commitRef = useRef(onCommit);
    useEffect(() => {
        commitRef.current = onCommit;
    }, [onCommit]);
    // Whether the last stop was ours. The API ends a session on its own after
    // a silence, and restarting it is what makes dictation feel continuous —
    // but restarting after a deliberate stop would be a microphone that will
    // not turn off.
    const wantsToListenRef = useRef(false);
    /** How many results of this recognition session have already been committed. */
    const committedCountRef = useRef(0);

    const build = useCallback((): SpeechRecognitionLike | null => {
        const Recognition = speechRecognitionConstructor();
        if (!Recognition) return null;

        const spoken = dictationLanguageOf(language);
        const recognition = new Recognition();
        recognition.lang = language;
        recognition.continuous = true;
        recognition.interimResults = true;

        recognition.onresult = (event) => {
            let pending = "";
            // Never re-read a phrase already committed, however far back this
            // event claims to start.
            const first = Math.max(event.resultIndex, committedCountRef.current);
            for (let i = first; i < event.results.length; i += 1) {
                const result = event.results[i];
                const text = result[0].transcript;
                if (result.isFinal) {
                    committedCountRef.current = i + 1;
                    const settled = normalizeDictatedText(text, spoken);
                    if (settled) commitRef.current(settled);
                } else {
                    pending += text;
                }
            }
            setInterim(normalizeDictatedText(pending, spoken));
        };

        recognition.onerror = (event) => {
            setError(
                event.error === "not-allowed" || event.error === "service-not-allowed"
                    ? "denied"
                    : "failed"
            );
            wantsToListenRef.current = false;
            setListening(false);
        };

        recognition.onend = () => {
            setInterim("");
            // The next session numbers its results from zero.
            committedCountRef.current = 0;
            if (wantsToListenRef.current) {
                // A silence ended the session, not the therapist.
                try {
                    recognition.start();
                    return;
                } catch {
                    // Some builds refuse an immediate restart; fall through and
                    // let the control show as stopped rather than lying.
                }
            }
            setListening(false);
        };

        return recognition;
    }, [language]);

    const start = useCallback(() => {
        setError(null);
        if (!supported) {
            setError("unsupported");
            return;
        }
        if (!recognitionRef.current) {
            recognitionRef.current = build();
        }
        try {
            wantsToListenRef.current = true;
            committedCountRef.current = 0;
            recognitionRef.current?.start();
            setListening(true);
        } catch {
            // Already running: the therapist pressed twice, which is not a
            // failure and should not be reported as one.
            setListening(true);
        }
    }, [build, supported]);

    const stop = useCallback(() => {
        wantsToListenRef.current = false;
        recognitionRef.current?.stop();
        setListening(false);
        setInterim("");
    }, []);

    // Navigating away must not leave the microphone open.
    useEffect(
        () => () => {
            wantsToListenRef.current = false;
            recognitionRef.current?.abort();
            recognitionRef.current = null;
        },
        []
    );

    return { supported, listening, interim, error, start, stop };
}
